import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Button } from './ui/button';
import { Check, ChevronDown } from 'lucide-react';
import { matchesSearch } from '@/lib/search';

// Shared searchable + alphabetically-sorted editor picker. Reuses the existing
// cmdk Command + Popover primitives (same pattern as the dashboard client
// picker) — no new library. DISPLAY/SORT/FILTER only: it emits the selected
// editor id (or null for "Unassigned") via onChange; each call site maps that
// to its own save contract. The assignment-save logic stays at the call site.
const byName = (a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });

// `out_until` is the LAST day away; show the first day BACK, which is the date
// an admin is actually deciding against. Stamped on the editor by
// GET /users/editors, so every surface that lists editors gets it without its
// own fetch. Local midnight so it never slips a day.
const backOn = (ymd) => {
  try {
    const d = new Date(ymd + 'T00:00:00');
    d.setDate(d.getDate() + 1);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ymd; }
};

export function EditorSelect({
  value,                       // current editor id, or null/'' for unassigned
  editors = [],
  onChange,                    // (editorId | null) => void  — caller maps null to its sentinel
  placeholder = 'Unassigned',
  includeUnassigned = true,
  unassignedLabel = 'Unassigned',
  triggerClassName = '',
  testId,
}) {
  const [open, setOpen] = useState(false);
  // A restricted editor cannot sign in, so nobody can be given to them (Ryan, 2026-10-01: "make editors not show
  // up in the list if we don't use them anymore"). The one already on this job stays visible so its name shows.
  const sorted = editors.filter(e => !e.restricted || String(e.id) === String(value)).sort(byName);
  const selected = editors.find(e => String(e.id) === String(value));
  const select = (id) => { onChange(id); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} data-testid={testId}
          className={`justify-between bg-ug-surface border-ug-border text-zinc-100 font-normal ${triggerClassName}`}>
          {/* The closed trigger carries it too — the drawer shows the assigned
              editor without anyone opening the list, and an editor going away
              AFTER assignment is the case that otherwise goes unnoticed. */}
          <span className={`truncate ${selected ? 'text-zinc-100' : 'text-slate-500'}`}>
            {selected ? selected.name : placeholder}
            {selected?.out_until && (
              <span className="ml-1.5 text-[9px] font-mono text-orange-400" data-testid="editor-select-out-until">
                · back {backOn(selected.out_until)}
              </span>
            )}
          </span>
          <ChevronDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0 bg-ug-raised border-ug-border-strong" align="start">
        <Command
          filter={(itemValue, search, keywords) => {
            // Case-insensitive LITERAL match against the editor NAME, per TERM.
            // cmdk's default scoring is fuzzy/subsequence (it matched "all" ->
            // "RonALd BaLan"); every typed term must still appear as a literal
            // consecutive run, so that stays defeated. What changes is that
            // "ron b" now matches "Ronald Balan" — the terms are matched
            // independently instead of as one string, which also makes the
            // picker immune to stray whitespace inside a stored name. We match
            // the NAME (passed via keywords), never the id (item value is the
            // id, used only for stable identity/selection).
            const name = (keywords && keywords[0]) || itemValue || '';
            return matchesSearch(search, [name]) ? 1 : 0;
          }}
          className="bg-transparent">
          <CommandInput placeholder="Search editors..." className="h-9 text-sm"
            data-testid={testId ? `${testId}-search` : undefined} />
          <CommandList>
            <CommandEmpty className="py-3 text-center text-xs text-zinc-500">No editors found</CommandEmpty>
            <CommandGroup className="max-h-60 overflow-y-auto">
              {includeUnassigned && (
                <CommandItem value="__unassigned__" onSelect={() => select(null)}
                  className="text-slate-400 text-xs cursor-pointer" data-testid={testId ? `${testId}-opt-unassigned` : undefined}>
                  <Check className={`mr-2 h-3 w-3 ${!value ? 'opacity-100' : 'opacity-0'}`} />
                  {unassignedLabel}
                </CommandItem>
              )}
              {sorted.map(e => (
                <CommandItem key={e.id} value={e.id} keywords={[e.name]} onSelect={() => select(e.id)}
                  className="text-zinc-100 text-xs cursor-pointer">
                  <Check className={`mr-2 h-3 w-3 ${String(value) === String(e.id) ? 'opacity-100' : 'opacity-0'}`} />
                  <span className={`truncate ${e.out_until ? 'opacity-60' : ''}`}>{e.name}</span>
                  {e.out_until && (
                    <span data-testid={`es-out-until-${e.id}`}
                      className="tap-auto ml-1.5 shrink-0 text-[8px] font-mono bg-orange-400/15 text-orange-400 px-1 py-0.5 rounded whitespace-nowrap">
                      BACK {backOn(e.out_until)}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
